"use client";

import { cn } from "@/utils/cn";
import { getLocalTimeZone, today } from "@internationalized/date";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import {
	Button as AriaButton,
	Calendar as AriaCalendar,
	CalendarCell as AriaCalendarCell,
	CalendarCellProps as AriaCalendarCellProps,
	CalendarGrid as AriaCalendarGrid,
	CalendarGridBody as AriaCalendarGridBody,
	CalendarGridBodyProps as AriaCalendarGridBodyProps,
	CalendarGridHeader as AriaCalendarGridHeader,
	CalendarGridHeaderProps as AriaCalendarGridHeaderProps,
	CalendarGridProps as AriaCalendarGridProps,
	CalendarHeaderCell as AriaCalendarHeaderCell,
	CalendarHeaderCellProps as AriaCalendarHeaderCellProps,
	CalendarProps as AriaCalendarProps,
	Heading as AriaHeading,
	RangeCalendar as AriaRangeCalendar,
	RangeCalendarStateContext as AriaRangeCalendarStateContext,
	CalendarStateContext,
	composeRenderProps,
	DateValue,
	useLocale,
} from "react-aria-components";

function useCalendarStateContext() {
	const state = React.useContext(CalendarStateContext);
	if (!state) {
		throw new Error("Calendar components must be used inside <Calendar />");
	}
	return state;
}

const Calendar = AriaCalendar;

const RangeCalendar = AriaRangeCalendar;

const CalendarHeading = ({ mode }: { mode: CalendarMode }) => {
	const { direction } = useLocale();
	const state = useCalendarStateContext();

	let title: React.ReactNode;
	const isShowRTL = mode !== "month";

	if (mode === "day") {
		return (
			<header className="flex items-center justify-between px-6 py-4">
				<AriaButton slot="previous" className="size-8 p-0">
					{direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
				</AriaButton>
				<AriaHeading className="text-base font-medium" />
				<AriaButton slot="next" className="size-8 p-0">
					{direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
				</AriaButton>
			</header>
		);
	}

	if (mode === "month") {
		title = `Jan - Dec`;
	} else {
		const startYear = Math.floor(state.focusedDate.year / 12) * 12;
		title = `${startYear} – ${startYear + 11}`;
	}

	return (
		<header className="flex items-center justify-between px-6 py-1">
			{isShowRTL && (
				<button
					className="mr-2 cursor-pointer"
					onClick={() =>
						state.setFocusedDate(state.focusedDate.subtract(mode === "year" ? { years: 12 } : { years: 1 }))
					}
				>
					{direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
				</button>
			)}

			<span className="text-base font-medium">{title}</span>

			{isShowRTL && (
				<button
					className="ml-2 cursor-pointer"
					onClick={() => state.setFocusedDate(state.focusedDate.add(mode === "year" ? { years: 12 } : { years: 1 }))}
				>
					{direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
				</button>
			)}
		</header>
	);
};

const CalendarGrid = ({ className, ...props }: AriaCalendarGridProps) => (
	<AriaCalendarGrid className={cn("w-full border-collapse", className)} {...props} />
);

const CalendarGridHeader = ({ className, ...props }: AriaCalendarGridHeaderProps) => (
	<AriaCalendarGridHeader className={cn("[&>tr]:flex", className)} {...props} />
);

const CalendarHeaderCell = ({ className, ...props }: AriaCalendarHeaderCellProps) => (
	<AriaCalendarHeaderCell
		className={cn("w-10 h-10 flex items-center justify-center text-sm font-medium text-neutral-600", className)}
		{...props}
	/>
);

const CalendarGridBody = ({ className, ...props }: AriaCalendarGridBodyProps) => (
	<AriaCalendarGridBody className={cn("[&>tr]:flex [&>tr>td]:p-0", className)} {...props} />
);

const CalendarCell = ({ className, ...props }: AriaCalendarCellProps) => {
	const isRange = Boolean(React.useContext(AriaRangeCalendarStateContext));
	return (
		<AriaCalendarCell
			className={composeRenderProps(className, (className, renderProps) =>
				cn(
					"relative flex size-10 items-center justify-center p-0 text-sm font-medium rounded-lg transition-colors cursor-pointer",
					/* Default state */
					"text-neutral-700 hover:bg-accent",
					/* Disabled */
					renderProps.isDisabled && "text-neutral-400 cursor-not-allowed hover:bg-transparent",
					/* Selected */
					renderProps.isSelected &&
						"bg-primary text-primary-foreground hover:bg-primary data-[focused]:bg-primary data-[focused]:text-primary-foreground",
					/* Selection Start/End for ranges */
					renderProps.isSelected &&
						isRange &&
						!renderProps.isSelectionStart &&
						!renderProps.isSelectionEnd &&
						"rounded-none bg-primary/10 text-primary hover:bg-primary/20",
					/* Outside Month */
					renderProps.isOutsideMonth &&
						"text-neutral-400 hover:bg-transparent data-[selected]:bg-primary/50 data-[selected]:text-primary-foreground",
					/* Current Date */
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					renderProps.date.compare(today(getLocalTimeZone()) as any) === 0 &&
						!renderProps.isSelected &&
						"bg-accent text-accent-foreground font-semibold",
					/* Unavailable Date */
					renderProps.isUnavailable && "cursor-not-allowed text-destructive opacity-50",
					renderProps.isInvalid &&
						"bg-destructive text-destructive-foreground data-[focused]:bg-destructive data-[hovered]:bg-destructive",
					className,
				),
			)}
			{...props}
		/>
	);
};

export type CalendarMode = "day" | "month" | "year";

const DayCalendarBody = () => (
	<CalendarGrid>
		<CalendarGridHeader>{day => <CalendarHeaderCell>{day}</CalendarHeaderCell>}</CalendarGridHeader>
		<CalendarGridBody>{date => <CalendarCell date={date} />}</CalendarGridBody>
	</CalendarGrid>
);

const MonthCalendarBody = () => {
	const state = useCalendarStateContext();
	const months = Array.from({ length: 12 }, (_, i) => i + 1);

	return (
		<div className="grid grid-cols-3 gap-2 p-2">
			{months.map(month => {
				const isSelected = state.focusedDate.month === month;

				return (
					<button
						key={month}
						className={cn(
							"p-1 rounded-lg text-sm font-medium cursor-pointer",
							isSelected ? "bg-primary text-primary-foreground" : "hover:bg-accent",
						)}
						onClick={() => {
							const newDate = state.focusedDate.set({ month });
							state.setFocusedDate(newDate);
							state.selectDate(newDate);
						}}
					>
						{new Intl.DateTimeFormat(undefined, {
							month: "short",
						}).format(new Date(2024, month - 1))}
					</button>
				);
			})}
		</div>
	);
};

const YearCalendarBody = () => {
	const state = useCalendarStateContext();
	// Snap to 12-year grid to prevent shifting
	const startYear = Math.floor(state.focusedDate.year / 12) * 12;
	const years = Array.from({ length: 12 }, (_, i) => startYear + i);

	return (
		<div className="grid grid-cols-3 gap-2 p-4">
			{years.map(year => {
				const isSelected = state.focusedDate.year === year;

				return (
					<button
						key={year}
						className={cn(
							"p-1 rounded-lg text-sm font-medium cursor-pointer",
							isSelected ? "bg-primary text-primary-foreground" : "hover:bg-accent",
						)}
						onClick={() => {
							const newDate = state.focusedDate.set({ year });
							state.setFocusedDate(newDate);
							state.selectDate(newDate);
						}}
					>
						{year}
					</button>
				);
			})}
		</div>
	);
};

function JollyCalendar<T extends DateValue>({
	mode = "day",
	...props
}: AriaCalendarProps<T> & { mode?: CalendarMode }) {
	return (
		<Calendar {...props}>
			<CalendarHeading mode={mode} />

			{mode === "day" && <DayCalendarBody />}
			{mode === "month" && <MonthCalendarBody />}
			{mode === "year" && <YearCalendarBody />}
		</Calendar>
	);
}

export {
	Calendar,
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHeader,
	CalendarHeaderCell,
	CalendarHeading,
	JollyCalendar,
	RangeCalendar,
};
